import * as React from 'react';
import { Icon, Image, ITagBasicsProps, ITagProps } from 'telia-front-react';
import { Prices, Product, ProductTag } from '../content/ProductInterfaces';
import { Language } from '../stores/LanguageStore';
import { getImageSrcset } from './PathUtil';
import { calculateDiscountPercentage, calculateDiscountPrice } from './PriceUtil';
import { localizeString, removeInvalidSymbolsForId } from './ProductUtil';

const ZERO_INTEREST_TAG_NAME = 'Järelmaks 0% intress';

export const mapTags = (product: Product, language: Language, prices?: Prices): ITagProps[] => {
  if (product.tags && product.tags.length) {
    return product.tags
      .filter((tag) => tag.tagNames.et !== ZERO_INTEREST_TAG_NAME && dynamicProductHasDiscount(prices, tag))
      .map((tag, i) => (tag.tagNames ? buildTagProps(tag, product, language, i, product.ean, prices) : null));
  }
  return null;
};

const buildTagProps = (tag: ProductTag, product: Product, language: Language, index?: number, ean?: string, prices?: Prices): ITagProps => {
  const tagText = buildTagText(tag, language, prices);
  return {
    text: renderTagContent(tag, tagText),
    fontSize: 'small',
    responsive: [
      {
        breakPoint: 'xs',
        props: {
          size: 'medium',
          fontSize: 'small',
        },
      },
    ],
    purpose: tag.colorType as ITagBasicsProps['purpose'],
    tooltip: tag.imageUrl && {
      id: `tag-tooltip-${removeInvalidSymbolsForId(product.ean ?? product.productCode)}-${index}`,
      children: localizeString(tag.tagNames, language),
    },
  };
};

const renderTagContent = (tag: ProductTag, tagText: string) => {
  if (tag.imageUrl) {
    return <Image srcset={getImageSrcset(['50', '100'], tag.imageUrl)} align="center" alt={tagText} />;
  } else if (tag.icon) {
    return (
      <>
        <Icon modifiers={`#${tag.icon}`} size={'sm'} />
        {tagText}
      </>
    );
  }
  return tagText;
};

const buildTagText = (tag: ProductTag, language: Language, prices?: Prices): string => {
  if (tag.dynamicType) {
    const dynamicDiscount = calculateDiscount(tag.dynamicType, prices);
    return localizeString(tag.tagNames, language).replace('${value}', dynamicDiscount);
  }
  return localizeString(tag.tagNames, language);
};

const calculateDiscount = (tagDynamicType: string, prices: Prices): string => {
  switch (tagDynamicType) {
    case 'DISCOUNT':
      return `${calculateDiscountPrice(prices)}`;
    case 'PERCENTAGE':
      return `${calculateDiscountPercentage(prices)}`;
    default:
      return '';
  }
};

const dynamicProductHasDiscount = (prices: Prices, tag: ProductTag): boolean => {
  return !tag.dynamicType || (!!prices.previouslyBestPrice?.amount && prices.previouslyBestPrice.amount > prices.currentPrice.amount);
};
